import React, { useState, useCallback, useEffect } from 'react';


// Import config service
import {
    fetchFunds,
    fetchProviders,
    fetchProviderConfig,
    fetchSettings,
    fetchQuestions,
    saveProviderConfig,
    saveSettings,
    saveQuestions,
    reloadConfig
} from '../../services/configService';

// Settings metadata for display
const SETTINGS_META = {
    // Tax
    pension_annual_allowance: { label: "Pension Annual Allowance", unit: "£", category: "tax" },
    isa_annual_allowance: { label: "ISA Annual Allowance", unit: "£", category: "tax" },
    high_earner_warning_threshold: { label: "High Earner Warning", unit: "£", category: "tax" },
    taper_threshold_income: { label: "Taper Threshold Income", unit: "£", category: "tax" },
    taper_adjusted_income: { label: "Taper Adjusted Income", unit: "£", category: "tax" },
    taper_minimum_allowance: { label: "Taper Minimum Allowance", unit: "£", category: "tax" },
    basic_rate_threshold: { label: "Basic Rate Threshold", unit: "£", category: "tax" },
    higher_rate_threshold: { label: "Higher Rate Threshold", unit: "£", category: "tax" },
    basic_rate_relief: { label: "Basic Rate Relief", unit: "%", multiplier: 100, category: "tax" },
    higher_rate_relief: { label: "Higher Rate Relief", unit: "%", multiplier: 100, category: "tax" },
    additional_rate_relief: { label: "Additional Rate Relief", unit: "%", multiplier: 100, category: "tax" },

    // Projection
    default_growth_rate: { label: "Default Growth Rate", unit: "%", multiplier: 100, category: "projection" },
    overfunding_threshold: { label: "Overfunding Threshold", unit: "%", multiplier: 100, category: "projection" },
    safe_withdrawal_rate: { label: "Safe Withdrawal Rate", unit: "%", multiplier: 100, category: "projection" },
    state_pension_annual: { label: "State Pension (Annual)", unit: "£", category: "projection" },

    // Contribution
    max_pct_net_income: { label: "Max % of Net Income", unit: "%", multiplier: 100, category: "contribution" },
    max_pct_disposable: { label: "Max % of Disposable", unit: "%", multiplier: 100, category: "contribution" },
    max_total_pct_disposable: { label: "Max Total % Disposable", unit: "%", multiplier: 100, category: "contribution" },
    min_monthly_contribution: { label: "Min Monthly Contribution", unit: "£", category: "contribution" },
    min_disposable_for_advice: { label: "Min Disposable for Advice", unit: "£", category: "contribution" },

    // Risk
    risk_tolerance_min: { label: "Tolerance Min Score", unit: "", category: "risk" },
    risk_tolerance_max: { label: "Tolerance Max Score", unit: "", category: "risk" },
    risk_capacity_min: { label: "Capacity Min Score", unit: "", category: "risk" },
    risk_capacity_max: { label: "Capacity Max Score", unit: "", category: "risk" },
    near_retirement_threshold_years: { label: "Near Retirement (years)", unit: "yrs", category: "risk" },
    near_retirement_adjustment: { label: "Near Retirement Adj.", unit: "x", category: "risk" },
    approaching_retirement_years: { label: "Approaching Retirement", unit: "yrs", category: "risk" },
    approaching_retirement_adjustment: { label: "Approaching Ret. Adj.", unit: "x", category: "risk" },

    // Compliance
    equity_ter_threshold: { label: "Equity TER Threshold", unit: "%", category: "compliance" },
    bond_ter_threshold: { label: "Bond TER Threshold", unit: "%", category: "compliance" },
    cash_ter_threshold: { label: "Cash TER Threshold", unit: "%", category: "compliance" },
};

const sumRow = (row) => Object.values(row).reduce((a, b) => a + b, 0);
const assetColors = { Equity: "#2563eb", Bond: "#f59e0b", Cash: "#10b981", Mixed: "#8b5cf6" };

const cellInput = { background: "#1e293b", border: "1px solid #334155", borderRadius: 6, padding: "6px 8px", color: "#e2e8f0", fontSize: 13, boxSizing: "border-box" };
const thStyle = { textAlign: "left", padding: "10px 8px", color: "#94a3b8", fontWeight: 500, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap" };

function SectionTitle({ children }) {
    return <h2 style={{ fontSize: 18, fontWeight: 700, color: "#e2e8f0", margin: "0 0 20px", letterSpacing: -0.3 }}>{children}</h2>;
}

function PerfCell({ v }) {
    const positive = v >= 0;
    return <span style={{ fontFamily: "monospace", fontSize: 12, color: positive ? "#10b981" : "#ef4444" }}>{positive ? "+" : ""}{v?.toFixed(2)}%</span>;
}

function SettingsField({ settingKey, value, onChange, meta }) {
    const displayValue = meta.multiplier ? value * meta.multiplier : value;

    const handleChange = (e) => {
        let newValue = parseFloat(e.target.value) || 0;
        if (meta.multiplier) {
            newValue = newValue / meta.multiplier;
        }
        onChange(settingKey, newValue);
    };

    return (
        <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 0",
            borderBottom: "1px solid #1e293b"
        }}>
            <label style={{ fontSize: 13, color: "#cbd5e1" }}>{meta.label}</label>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                    type="number"
                    value={displayValue}
                    onChange={handleChange}
                    step={meta.multiplier ? 1 : (meta.unit === "x" ? 0.05 : 1)}
                    style={{
                        ...cellInput,
                        width: 100,
                        textAlign: "right",
                        fontFamily: "monospace"
                    }}
                />
                <span style={{ fontSize: 12, color: "#64748b", minWidth: 24 }}>{meta.unit}</span>
            </div>
        </div>
    );
}

function getShortFundName(fullName) {
    if (fullName.includes("UK Equity")) return "UK Equity";
    if (fullName.includes("World (ex UK)") || fullName.includes("World ex UK")) return "World ex-UK Equity";
    if (fullName.includes("Emerging Markets")) return "EM Equity";
    if (fullName.includes("Overseas Bond") || fullName.includes("Global Bond")) return "Global Bonds";
    if (fullName.includes("UK Gilt")) return "UK Gilts";
    if (fullName.includes("Corporate Bond")) return "Corp Bonds";
    if (fullName.includes("Cash") || fullName.includes("Money Market")) return "Cash";
    return fullName.split(" ").slice(-3).join(" ");
}

function LoadingSpinner() {
    return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
            <div style={{
                width: 32, height: 32,
                border: "3px solid #334155",
                borderTopColor: "#6366f1",
                borderRadius: "50%",
                animation: "spin 1s linear infinite"
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}

// =============================================================================
// Main Component
// =============================================================================

const WaiveAdmin = () => {
    const [activeTab, setActiveTab] = useState("provider");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Config state - loaded from API
    const [providers, setProviders] = useState({});
    const [funds, setFunds] = useState([]);
    const [selectedProvider, setSelectedProvider] = useState("LG");
    const [providerConfig, setProviderConfig] = useState(null);
    const [settings, setSettings] = useState({});
    const [questionsConfig, setQuestionsConfig] = useState(null);

    // Derived state from provider config
    const [matrix, setMatrix] = useState({});
    const [riskBands, setRiskBands] = useState([]);
    const [portfolioLevels, setPortfolioLevels] = useState({});

    const [toast, setToast] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [settingsCategory, setSettingsCategory] = useState("tax");

    // ─── Load Config using configService ───────────────────────────────────
    const loadConfig = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Fetch all config in parallel using configService
            const [fundsData, providersData, settingsData, questionsData] = await Promise.all([
                fetchFunds(),
                fetchProviders(),
                fetchSettings(),
                fetchQuestions(),
            ]);

            setFunds(fundsData || []);
            setProviders(providersData.providers || {});
            setSettings(settingsData || {});
            setQuestionsConfig(questionsData || null);

            // Load first provider's full config
            const defaultProvider = providersData.default_provider || "LG";
            setSelectedProvider(defaultProvider);
            await loadProviderConfigData(defaultProvider);

        } catch (err) {
            console.error('Config fetch error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadProviderConfigData = async (code) => {
        try {
            const data = await fetchProviderConfig(code);
            setProviderConfig(data);
            setMatrix(data.allocation_matrix || {});
            setRiskBands((data.risk_bands || []).map(b => ({
                label: b.label,
                minScore: b.min_score,
                maxScore: b.max_score,
                maxSRRI: b.max_srri,
                portfolio: b.portfolio
            })));
            setPortfolioLevels(data.portfolio_levels || {});
        } catch (err) {
            console.error('Provider config fetch error:', err);
        }
    };

    const saveConfig = async () => {
        // Validate matrix rows sum to 100
        for (const [lvl, row] of Object.entries(matrix)) {
            const s = sumRow(row);
            if (Math.abs(s - 100) > 0.5) {
                showToast(`Risk level ${lvl} sums to ${s.toFixed(2)}% — must be 100%`, "error");
                return;
            }
        }

        // Validate question weights sum to 1.0
        if (questionsConfig) {
            const rtQuestions = questionsConfig.tolerance?.questions || [];
            const cflQuestions = questionsConfig.capacity?.questions || [];
            const rtSum = rtQuestions.reduce((sum, q) => sum + (q.weight || 0), 0);
            const cflSum = cflQuestions.reduce((sum, q) => sum + (q.weight || 0), 0);

            if (Math.abs(rtSum - 1.0) > 0.01) {
                showToast(`Risk Tolerance weights sum to ${(rtSum * 100).toFixed(0)}% — must be 100%`, "error");
                return;
            }
            if (Math.abs(cflSum - 1.0) > 0.01) {
                showToast(`Capacity for Loss weights sum to ${(cflSum * 100).toFixed(0)}% — must be 100%`, "error");
                return;
            }

            // Validate RT/CFL overall weights sum to 1.0
            const rtWeight = questionsConfig.methodology?.tolerance_weight || 0;
            const cflWeight = questionsConfig.methodology?.capacity_weight || 0;
            if (Math.abs(rtWeight + cflWeight - 1.0) > 0.01) {
                showToast(`RT/CFL weights sum to ${((rtWeight + cflWeight) * 100).toFixed(0)}% — must be 100%`, "error");
                return;
            }
        }

        try {
            // Save provider config using configService
            const providerData = {
                ...providerConfig,
                allocation_matrix: matrix,
                risk_bands: riskBands.map(b => ({
                    label: b.label,
                    min_score: b.minScore,
                    max_score: b.maxScore,
                    max_srri: b.maxSRRI,
                    portfolio: b.portfolio
                })),
                portfolio_levels: portfolioLevels,
            };

            await saveProviderConfig(selectedProvider, providerData);
            await saveSettings(settings);

            // Save questions config if it exists
            if (questionsConfig) {
                await saveQuestions(questionsConfig);
            }

            setDirty(false);
            showToast("Configuration saved successfully");
        } catch (err) {
            console.error('Save error:', err);
            showToast("Save failed - " + err.message, "error");
        }
    };

    const handleReload = async () => {
        try {
            await reloadConfig();
            await loadConfig();
            showToast("Configuration reloaded from disk");
        } catch (err) {
            showToast("Reload failed - " + err.message, "error");
        }
    };

    // Load config on mount
    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    // Reload provider config when selection changes
    useEffect(() => {
        if (selectedProvider && !loading) {
            loadProviderConfigData(selectedProvider);
        }
    }, [selectedProvider]);

    const showToast = useCallback((msg, type = "success") => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 2800);
    }, []);

    const mark = () => setDirty(true);

    const updateMatrix = (level, fundId, value) => {
        const v = parseFloat(value) || 0;
        setMatrix(prev => ({ ...prev, [level]: { ...prev[level], [fundId]: v } }));
        mark();
    };

    const updateRiskBand = (idx, field, value) => {
        const next = [...riskBands];
        next[idx] = { ...next[idx], [field]: field === "label" ? value : parseInt(value) || 0 };
        setRiskBands(next);
        mark();
    };

    const updateSetting = (key, value) => {
        // Find which category this key belongs to
        const meta = SETTINGS_META[key];
        if (meta) {
            setSettings(prev => ({
                ...prev,
                [meta.category]: {
                    ...prev[meta.category],
                    [key]: value
                }
            }));
        }
        mark();
    };

    const getSettingValue = (key) => {
        const meta = SETTINGS_META[key];
        if (meta && settings[meta.category]) {
            return settings[meta.category][key] ?? 0;
        }
        return 0;
    };

    const getSettingsByCategory = (category) => {
        return Object.entries(SETTINGS_META)
            .filter(([_, meta]) => meta.category === category)
            .map(([key, meta]) => ({ key, meta, value: getSettingValue(key) }));
    };

    const handleExportJSON = () => {
        const config = {
            provider: providerConfig,
            funds,
            allocationMatrix: matrix,
            riskBands,
            portfolioLevels,
            settings
        };
        const blob = new Blob([JSON.stringify(config, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `waive-config-${selectedProvider.toLowerCase()}.json`;
        a.click();
        URL.revokeObjectURL(url);
        showToast("Exported configuration JSON");
    };

    const settingsCategories = [
        { id: "tax", label: "Tax & Allowances", icon: "💰" },
        { id: "projection", label: "Projections", icon: "📈" },
        { id: "contribution", label: "Contributions", icon: "💵" },
        { id: "risk", label: "Risk Adjustments", icon: "⚖️" },
        { id: "compliance", label: "Compliance", icon: "✓" },
    ];

    const tabs = [
        { id: "provider", label: "Provider" },
        { id: "funds", label: "Fund Universe" },
        { id: "matrix", label: "Allocation Matrix" },
        { id: "risk", label: "Risk Bands" },
        { id: "settings", label: "Settings" },
    ];

    // Get provider's funds
    const providerFunds = providerConfig?.available_funds
        ? funds.filter(f => providerConfig.available_funds.includes(f.id))
        : funds;

    if (loading) {
        return (
            <>

                <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", color: "#e2e8f0" }}>
                    <div style={{ maxWidth: 1280, margin: "0 auto", padding: "32px 24px" }}>
                        <LoadingSpinner />
                        <p style={{ textAlign: "center", color: "#64748b" }}>Loading configuration...</p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", color: "#e2e8f0" }}>
                <div style={{ maxWidth: 1280, margin: "0 auto", padding: "32px 24px" }}>

                    {/* Page Title */}
                    <h1 style={{ fontSize: 24, fontWeight: 700, color: "#e2e8f0", marginBottom: 24 }}>Configuration</h1>

                    {/* Error Banner */}
                    {error && (
                        <div style={{ background: "#7f1d1d", border: "1px solid #991b1b", borderRadius: 8, padding: "12px 16px", marginBottom: 24, color: "#fecaca" }}>
                            ⚠️ {error}
                        </div>
                    )}

                    {/* Provider Selector Row */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <label style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>Provider</label>
                            <select
                                value={selectedProvider}
                                onChange={e => { setSelectedProvider(e.target.value); setDirty(false); }}
                                style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 8, padding: "8px 14px", color: "#e2e8f0", fontSize: 14, cursor: "pointer", minWidth: 180 }}
                            >
                                {Object.entries(providers).map(([code, p]) => (
                                    <option key={code} value={code}>{p.name}</option>
                                ))}
                            </select>
                            <button onClick={handleReload}
                                    style={{ background: "transparent", border: "1px solid #334155", borderRadius: 8, padding: "8px 12px", color: "#94a3b8", fontSize: 12, cursor: "pointer" }}>
                                ↻ Reload
                            </button>
                        </div>
                        <div style={{ display: "flex", gap: 10 }}>
                            <button onClick={handleExportJSON}
                                    style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 8, padding: "10px 18px", color: "#94a3b8", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                                Export JSON
                            </button>
                            <button onClick={saveConfig}
                                    style={{ background: dirty ? "#10b981" : "#334155", border: "none", borderRadius: 8, padding: "10px 18px", color: "#fff", fontSize: 12, fontWeight: 600, cursor: dirty ? "pointer" : "default", opacity: dirty ? 1 : 0.5 }}>
                                {dirty ? "Save Changes" : "Saved"}
                            </button>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid #334155", paddingBottom: 12 }}>
                        {tabs.map(t => (
                            <button
                                key={t.id}
                                onClick={() => setActiveTab(t.id)}
                                style={{
                                    background: activeTab === t.id ? "#334155" : "transparent",
                                    border: "none",
                                    borderRadius: 8,
                                    padding: "10px 18px",
                                    color: activeTab === t.id ? "#e2e8f0" : "#64748b",
                                    fontSize: 13,
                                    fontWeight: 500,
                                    cursor: "pointer",
                                    transition: "all .15s"
                                }}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {/* Content Card */}
                    <div style={{ background: "#0f172a", borderRadius: 16, border: "1px solid #1e293b", padding: 28 }}>

                        {/* Provider Tab */}
                        {activeTab === "provider" && providerConfig && (
                            <div>
                                <SectionTitle>Provider Configuration — {providerConfig.name}</SectionTitle>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, maxWidth: 560 }}>
                                    <div>
                                        <label style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4, display: "block", textTransform: "uppercase" }}>Provider Name</label>
                                        <div style={{ padding: "8px 12px", background: "#1e293b", borderRadius: 8, color: "#e2e8f0" }}>{providerConfig.name}</div>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4, display: "block", textTransform: "uppercase" }}>Code</label>
                                        <div style={{ padding: "8px 12px", background: "#1e293b", borderRadius: 8, color: "#e2e8f0" }}>{providerConfig.code}</div>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4, display: "block", textTransform: "uppercase" }}>Max Funds</label>
                                        <div style={{ padding: "8px 12px", background: "#1e293b", borderRadius: 8, color: "#e2e8f0" }}>{providerConfig.max_funds}</div>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4, display: "block", textTransform: "uppercase" }}>Rebalance</label>
                                        <div style={{ padding: "8px 12px", background: "#1e293b", borderRadius: 8, color: "#e2e8f0" }}>{providerConfig.rebalance_frequency}</div>
                                    </div>
                                </div>
                                <div style={{ marginTop: 24 }}>
                                    <label style={{ fontSize: 11, color: "#94a3b8", marginBottom: 8, display: "block", textTransform: "uppercase", letterSpacing: 0.5 }}>Supported Wrappers</label>
                                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                        {(providerConfig.wrappers || []).map(w => (
                                            <span key={w} style={{ padding: "6px 14px", borderRadius: 20, border: "1px solid #818cf8", background: "rgba(99,102,241,.15)", color: "#a5b4fc", fontSize: 12 }}>
                                                ✓ {w}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <div style={{ marginTop: 24 }}>
                                    <label style={{ fontSize: 11, color: "#94a3b8", marginBottom: 8, display: "block", textTransform: "uppercase", letterSpacing: 0.5 }}>Available Funds ({providerConfig.available_funds?.length || 0})</label>
                                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                        {(providerConfig.available_funds || []).map(fid => {
                                            const fund = funds.find(f => f.id === fid);
                                            return (
                                                <span key={fid} style={{ padding: "4px 10px", borderRadius: 6, background: "#334155", color: "#94a3b8", fontSize: 11 }}>
                                                    {fund?.short_name || fid}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Fund Universe Tab */}
                        {activeTab === "funds" && (
                            <div>
                                <SectionTitle>Fund Universe ({funds.length} funds)</SectionTitle>
                                <div style={{ overflowX: "auto" }}>
                                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                                        <thead>
                                        <tr style={{ borderBottom: "1px solid #334155" }}>
                                            {["ID", "Name", "ISIN", "SRRI", "TER %", "Class", "Geo", "1Y %", "3Y %", "5Y %"].map(h => (
                                                <th key={h} style={thStyle}>{h}</th>
                                            ))}
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {funds.map((f) => (
                                            <tr key={f.id} style={{ borderBottom: "1px solid #1e293b" }}>
                                                <td style={{ padding: "8px", color: "#64748b", fontFamily: "monospace", fontSize: 11 }}>{f.id}</td>
                                                <td style={{ padding: "8px", color: "#e2e8f0" }}>{f.name}</td>
                                                <td style={{ padding: "8px", fontFamily: "monospace", fontSize: 11, color: "#94a3b8" }}>{f.isin}</td>
                                                <td style={{ padding: "8px", textAlign: "center" }}>
                                                    <span style={{ background: "#334155", padding: "2px 8px", borderRadius: 4, fontSize: 11 }}>{f.srri}</span>
                                                </td>
                                                <td style={{ padding: "8px", textAlign: "center", color: "#94a3b8" }}>{f.ter?.toFixed(2)}</td>
                                                <td style={{ padding: "8px" }}>
                                                    <span style={{ color: assetColors[f.asset_class] || "#94a3b8" }}>{f.asset_class}</span>
                                                </td>
                                                <td style={{ padding: "8px", color: "#94a3b8" }}>{f.geo}</td>
                                                <td style={{ padding: "8px" }}><PerfCell v={f.performance?.["1y"] || 0} /></td>
                                                <td style={{ padding: "8px" }}><PerfCell v={f.performance?.["3y"] || 0} /></td>
                                                <td style={{ padding: "8px" }}><PerfCell v={f.performance?.["5y"] || 0} /></td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Allocation Matrix Tab */}
                        {activeTab === "matrix" && (
                            <div>
                                <SectionTitle>Allocation Matrix — {providerConfig?.name}</SectionTitle>
                                <p style={{ fontSize: 12, color: "#64748b", marginBottom: 16 }}>
                                    Each portfolio level defines percentage allocations across funds. Rows must sum to 100%.
                                </p>

                                {/* Portfolio Level Descriptions */}
                                {Object.keys(portfolioLevels).length > 0 && (
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
                                        {Object.entries(portfolioLevels).map(([level, info]) => (
                                            <div key={level} style={{
                                                background: "#1e293b",
                                                borderRadius: 8,
                                                padding: "12px 14px",
                                                border: "1px solid #334155",
                                                fontSize: 11
                                            }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                                    <span style={{
                                                        background: `hsl(${220 - ((parseInt(level) - 1) / 9) * 180}, 60%, 45%)`,
                                                        color: "#fff",
                                                        padding: "2px 8px",
                                                        borderRadius: 4,
                                                        fontWeight: 700,
                                                        fontSize: 12
                                                    }}>
                                                        {level}
                                                    </span>
                                                    <span style={{ fontWeight: 600, color: "#e2e8f0" }}>{info.name}</span>
                                                </div>
                                                <p style={{ margin: 0, color: "#94a3b8", lineHeight: 1.4 }}>{info.description?.split('.')[0]}.</p>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div style={{ overflowX: "auto" }}>
                                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                                        <thead>
                                        <tr style={{ borderBottom: "1px solid #334155" }}>
                                            <th style={{ ...thStyle, minWidth: 100 }}>Level</th>
                                            {providerFunds.map(f => (
                                                <th key={f.id} style={{ ...thStyle, minWidth: 90, textAlign: "center" }}>
                                                    <div style={{ color: assetColors[f.asset_class] || "#94a3b8", fontSize: 10 }}>
                                                        {f.short_name || getShortFundName(f.name)}
                                                    </div>
                                                    <div style={{ fontSize: 9, color: "#64748b" }}>SRRI {f.srri}</div>
                                                </th>
                                            ))}
                                            <th style={{ ...thStyle, textAlign: "right" }}>Sum</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {Object.entries(matrix).sort(([a], [b]) => parseInt(a) - parseInt(b)).map(([level, row]) => {
                                            const s = sumRow(row);
                                            const ok = Math.abs(s - 100) < 0.5;
                                            const levelInfo = portfolioLevels[level] || {};
                                            return (
                                                <tr key={level} style={{ borderBottom: "1px solid #1e293b" }}>
                                                    <td style={{ padding: "8px" }}>
                                                        <div style={{ fontWeight: 600, color: "#e2e8f0" }}>Level {level}</div>
                                                        <div style={{ fontSize: 10, color: "#64748b" }}>{levelInfo.name}</div>
                                                    </td>
                                                    {providerFunds.map(f => (
                                                        <td key={f.id} style={{ padding: "4px" }}>
                                                            <input
                                                                type="number"
                                                                step={0.01}
                                                                value={row[f.id] ?? 0}
                                                                onChange={e => updateMatrix(level, f.id, e.target.value)}
                                                                style={{
                                                                    ...cellInput,
                                                                    width: "100%",
                                                                    textAlign: "right",
                                                                    background: (row[f.id] ?? 0) > 0 ? "#1e293b" : "#0f172a"
                                                                }}
                                                            />
                                                        </td>
                                                    ))}
                                                    <td style={{ padding: "8px", textAlign: "right", fontFamily: "monospace", fontWeight: 600, color: ok ? "#10b981" : "#ef4444" }}>
                                                        {s.toFixed(2)}%
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Allocation Visualization Bars */}
                                <div style={{ marginTop: 32 }}>
                                    <h4 style={{ fontSize: 13, fontWeight: 600, color: "#94a3b8", marginBottom: 16 }}>Allocation Visualization by Level</h4>
                                    {Object.entries(matrix).sort(([a], [b]) => parseInt(a) - parseInt(b)).map(([level, row]) => {
                                        const levelInfo = portfolioLevels[level] || {};
                                        return (
                                            <div key={level} style={{ marginBottom: 12 }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
                                                    <span style={{
                                                        fontSize: 11,
                                                        fontWeight: 600,
                                                        color: "#e2e8f0",
                                                        minWidth: 60
                                                    }}>
                                                        Level {level}
                                                    </span>
                                                    <span style={{ fontSize: 10, color: "#64748b" }}>{levelInfo.name}</span>
                                                </div>
                                                <div style={{ display: "flex", height: 24, borderRadius: 6, overflow: "hidden" }}>
                                                    {providerFunds.map(f => {
                                                        const pct = row[f.id] ?? 0;
                                                        if (pct === 0) return null;
                                                        return (
                                                            <div
                                                                key={f.id}
                                                                style={{
                                                                    width: `${pct}%`,
                                                                    background: assetColors[f.asset_class] || "#64748b",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    fontSize: 9,
                                                                    fontWeight: 600,
                                                                    color: "#fff",
                                                                    textShadow: "0 1px 2px rgba(0,0,0,.5)",
                                                                    minWidth: pct > 5 ? "auto" : 0,
                                                                    overflow: "hidden"
                                                                }}
                                                                title={`${f.short_name || getShortFundName(f.name)}: ${pct}%`}
                                                            >
                                                                {pct >= 10 ? `${pct.toFixed(0)}%` : ""}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {/* Legend */}
                                    <div style={{ display: "flex", gap: 16, marginTop: 16, flexWrap: "wrap" }}>
                                        {Object.entries(assetColors).map(([cls, color]) => (
                                            <div key={cls} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                <div style={{ width: 12, height: 12, borderRadius: 3, background: color }} />
                                                <span style={{ fontSize: 11, color: "#94a3b8" }}>{cls}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Risk Bands Tab */}
                        {activeTab === "risk" && (
                            <div>
                                {/* Weighted Methodology Overview */}
                                <SectionTitle>Mark's Weighted Scoring Methodology</SectionTitle>
                                <p style={{ fontSize: 12, color: "#64748b", marginBottom: 16 }}>
                                    Risk assessment uses weighted scoring. Edit the weights below — they are saved to <code style={{ background: "#334155", padding: "2px 6px", borderRadius: 4 }}>questions.json</code>
                                </p>

                                {/* Overall Weights */}
                                <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
                                    <div style={{ background: "#1e293b", borderRadius: 12, padding: 16, border: "1px solid #334155", flex: 1 }}>
                                        <label style={{ fontSize: 12, color: "#94a3b8", display: "block", marginBottom: 8 }}>Risk Tolerance Overall Weight</label>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            <input
                                                type="number"
                                                min={0}
                                                max={100}
                                                step={5}
                                                value={((questionsConfig?.methodology?.tolerance_weight || questionsConfig?.tolerance?.overall_weight || 0.4) * 100).toFixed(0)}
                                                onChange={(e) => {
                                                    const val = parseFloat(e.target.value) / 100;
                                                    setQuestionsConfig(prev => ({
                                                        ...prev,
                                                        methodology: { ...prev?.methodology, tolerance_weight: val, capacity_weight: 1 - val },
                                                        tolerance: { ...prev?.tolerance, overall_weight: val },
                                                        capacity: { ...prev?.capacity, overall_weight: 1 - val }
                                                    }));
                                                    setDirty(true);
                                                }}
                                                style={{ ...cellInput, width: 80, textAlign: "center", fontSize: 18, fontWeight: 600 }}
                                            />
                                            <span style={{ fontSize: 18, color: "#6366f1", fontWeight: 600 }}>%</span>
                                        </div>
                                    </div>
                                    <div style={{ background: "#1e293b", borderRadius: 12, padding: 16, border: "1px solid #334155", flex: 1 }}>
                                        <label style={{ fontSize: 12, color: "#94a3b8", display: "block", marginBottom: 8 }}>Capacity for Loss Overall Weight</label>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            <input
                                                type="number"
                                                min={0}
                                                max={100}
                                                step={5}
                                                value={((questionsConfig?.methodology?.capacity_weight || questionsConfig?.capacity?.overall_weight || 0.6) * 100).toFixed(0)}
                                                onChange={(e) => {
                                                    const val = parseFloat(e.target.value) / 100;
                                                    setQuestionsConfig(prev => ({
                                                        ...prev,
                                                        methodology: { ...prev?.methodology, capacity_weight: val, tolerance_weight: 1 - val },
                                                        capacity: { ...prev?.capacity, overall_weight: val },
                                                        tolerance: { ...prev?.tolerance, overall_weight: 1 - val }
                                                    }));
                                                    setDirty(true);
                                                }}
                                                style={{ ...cellInput, width: 80, textAlign: "center", fontSize: 18, fontWeight: 600 }}
                                            />
                                            <span style={{ fontSize: 18, color: "#10b981", fontWeight: 600 }}>%</span>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
                                    {/* Risk Tolerance Weights */}
                                    <div style={{ background: "#1e293b", borderRadius: 12, padding: 16, border: "1px solid #334155" }}>
                                        <h4 style={{ margin: "0 0 12px 0", fontSize: 13, color: "#6366f1" }}>
                                            📊 Risk Tolerance Questions
                                            <span style={{ fontSize: 11, color: "#64748b", fontWeight: 400, marginLeft: 8 }}>
                                                (must sum to 100%)
                                            </span>
                                        </h4>
                                        <table style={{ width: "100%", fontSize: 11 }}>
                                            <tbody>
                                            {(questionsConfig?.tolerance?.questions || []).map((q, i) => (
                                                <tr key={q.id || i}>
                                                    <td style={{ padding: "6px 0", color: "#94a3b8" }}>Q{i + 1}: {q.text?.substring(0, 30)}...</td>
                                                    <td style={{ textAlign: "right", width: 80 }}>
                                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                                                            <input
                                                                type="number"
                                                                min={0}
                                                                max={100}
                                                                step={5}
                                                                value={((q.weight || 0) * 100).toFixed(0)}
                                                                onChange={(e) => {
                                                                    const val = parseFloat(e.target.value) / 100;
                                                                    setQuestionsConfig(prev => {
                                                                        const questions = [...(prev?.tolerance?.questions || [])];
                                                                        questions[i] = { ...questions[i], weight: val };
                                                                        return { ...prev, tolerance: { ...prev?.tolerance, questions } };
                                                                    });
                                                                    setDirty(true);
                                                                }}
                                                                style={{ ...cellInput, width: 50, textAlign: "center", padding: "4px" }}
                                                            />
                                                            <span style={{ color: "#64748b" }}>%</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                            <tr style={{ borderTop: "1px solid #334155" }}>
                                                <td style={{ padding: "8px 0", color: "#e2e8f0", fontWeight: 600 }}>Total</td>
                                                <td style={{ textAlign: "right", fontWeight: 600 }}>
                                                    {(() => {
                                                        const sum = (questionsConfig?.tolerance?.questions || []).reduce((s, q) => s + (q.weight || 0), 0);
                                                        const pct = (sum * 100).toFixed(0);
                                                        const isValid = Math.abs(sum - 1) < 0.01;
                                                        return <span style={{ color: isValid ? "#10b981" : "#ef4444" }}>{pct}%</span>;
                                                    })()}
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Capacity for Loss Weights */}
                                    <div style={{ background: "#1e293b", borderRadius: 12, padding: 16, border: "1px solid #334155" }}>
                                        <h4 style={{ margin: "0 0 12px 0", fontSize: 13, color: "#10b981" }}>
                                            💰 Capacity for Loss Questions
                                            <span style={{ fontSize: 11, color: "#64748b", fontWeight: 400, marginLeft: 8 }}>
                                                (must sum to 100%)
                                            </span>
                                        </h4>
                                        <table style={{ width: "100%", fontSize: 11 }}>
                                            <tbody>
                                            {(questionsConfig?.capacity?.questions || []).map((q, i) => (
                                                <tr key={q.id || i}>
                                                    <td style={{ padding: "6px 0", color: "#94a3b8" }}>Q{i + 1}: {q.text?.substring(0, 30)}...</td>
                                                    <td style={{ textAlign: "right", width: 80 }}>
                                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                                                            <input
                                                                type="number"
                                                                min={0}
                                                                max={100}
                                                                step={5}
                                                                value={((q.weight || 0) * 100).toFixed(0)}
                                                                onChange={(e) => {
                                                                    const val = parseFloat(e.target.value) / 100;
                                                                    setQuestionsConfig(prev => {
                                                                        const questions = [...(prev?.capacity?.questions || [])];
                                                                        questions[i] = { ...questions[i], weight: val };
                                                                        return { ...prev, capacity: { ...prev?.capacity, questions } };
                                                                    });
                                                                    setDirty(true);
                                                                }}
                                                                style={{ ...cellInput, width: 50, textAlign: "center", padding: "4px" }}
                                                            />
                                                            <span style={{ color: "#64748b" }}>%</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                            <tr style={{ borderTop: "1px solid #334155" }}>
                                                <td style={{ padding: "8px 0", color: "#e2e8f0", fontWeight: 600 }}>Total</td>
                                                <td style={{ textAlign: "right", fontWeight: 600 }}>
                                                    {(() => {
                                                        const sum = (questionsConfig?.capacity?.questions || []).reduce((s, q) => s + (q.weight || 0), 0);
                                                        const pct = (sum * 100).toFixed(0);
                                                        const isValid = Math.abs(sum - 1) < 0.01;
                                                        return <span style={{ color: isValid ? "#10b981" : "#ef4444" }}>{pct}%</span>;
                                                    })()}
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* SRRI Bands */}
                                <SectionTitle>Combined Score → SRRI Mapping</SectionTitle>
                                <p style={{ fontSize: 12, color: "#64748b", marginBottom: 16 }}>
                                    Each answer is normalized (1→0, 2→0.33, 3→0.67, 4→1), weighted, and combined to produce SRRI 1-7.
                                </p>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, maxWidth: 700, marginBottom: 24 }}>
                                    <thead>
                                    <tr style={{ borderBottom: "1px solid #334155" }}>
                                        {["SRRI", "Min Score", "Max Score", "Risk Category"].map(h => (
                                            <th key={h} style={thStyle}>{h}</th>
                                        ))}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {(questionsConfig?.methodology?.srri_bands || []).map((band, i) => (
                                        <tr key={i} style={{ borderBottom: "1px solid #1e293b" }}>
                                            <td style={{ padding: "8px", textAlign: "center", fontWeight: 600, color: "#6366f1" }}>{band.srri}</td>
                                            <td style={{ padding: "8px" }}>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={1}
                                                    step={0.01}
                                                    value={band.min}
                                                    onChange={(e) => {
                                                        const val = parseFloat(e.target.value);
                                                        setQuestionsConfig(prev => {
                                                            const bands = [...(prev?.methodology?.srri_bands || [])];
                                                            bands[i] = { ...bands[i], min: val };
                                                            return { ...prev, methodology: { ...prev?.methodology, srri_bands: bands } };
                                                        });
                                                        setDirty(true);
                                                    }}
                                                    style={{ ...cellInput, width: 70, textAlign: "center" }}
                                                />
                                            </td>
                                            <td style={{ padding: "8px" }}>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={1}
                                                    step={0.01}
                                                    value={band.max}
                                                    onChange={(e) => {
                                                        const val = parseFloat(e.target.value);
                                                        setQuestionsConfig(prev => {
                                                            const bands = [...(prev?.methodology?.srri_bands || [])];
                                                            bands[i] = { ...bands[i], max: val };
                                                            return { ...prev, methodology: { ...prev?.methodology, srri_bands: bands } };
                                                        });
                                                        setDirty(true);
                                                    }}
                                                    style={{ ...cellInput, width: 70, textAlign: "center" }}
                                                />
                                            </td>
                                            <td style={{ padding: "8px", color: "#94a3b8" }}>
                                                {["Very Low", "Low", "Low-Medium", "Medium", "Medium-High", "High", "Very High"][i] || ""}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>

                                {/* Methodology Weights */}
                                <SectionTitle>Scoring Weights</SectionTitle>
                                <p style={{ fontSize: 12, color: "#64748b", marginBottom: 16 }}>
                                    How much each component contributes to the final risk score. Must sum to 100%.
                                </p>
                                <div style={{ background: "#1e293b", borderRadius: 12, padding: 16, border: "1px solid #334155", maxWidth: 400, marginBottom: 24 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #334155" }}>
                                        <label style={{ fontSize: 13, color: "#cbd5e1" }}>Risk Tolerance (RT)</label>
                                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                            <input
                                                type="number"
                                                min={0} max={100} step={5}
                                                value={((questionsConfig?.methodology?.tolerance_weight || 0.4) * 100).toFixed(0)}
                                                onChange={(e) => {
                                                    const rtPct = parseFloat(e.target.value) || 0;
                                                    setQuestionsConfig(prev => ({
                                                        ...prev,
                                                        methodology: {
                                                            ...prev?.methodology,
                                                            tolerance_weight: rtPct / 100,
                                                            capacity_weight: (100 - rtPct) / 100
                                                        }
                                                    }));
                                                    setDirty(true);
                                                }}
                                                style={{ ...cellInput, width: 70, textAlign: "right", fontFamily: "monospace" }}
                                            />
                                            <span style={{ fontSize: 12, color: "#64748b" }}>%</span>
                                        </div>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0" }}>
                                        <label style={{ fontSize: 13, color: "#cbd5e1" }}>Capacity for Loss (CFL)</label>
                                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                            <input
                                                type="number"
                                                min={0} max={100} step={5}
                                                value={((questionsConfig?.methodology?.capacity_weight || 0.6) * 100).toFixed(0)}
                                                onChange={(e) => {
                                                    const cflPct = parseFloat(e.target.value) || 0;
                                                    setQuestionsConfig(prev => ({
                                                        ...prev,
                                                        methodology: {
                                                            ...prev?.methodology,
                                                            capacity_weight: cflPct / 100,
                                                            tolerance_weight: (100 - cflPct) / 100
                                                        }
                                                    }));
                                                    setDirty(true);
                                                }}
                                                style={{ ...cellInput, width: 70, textAlign: "right", fontFamily: "monospace" }}
                                            />
                                            <span style={{ fontSize: 12, color: "#64748b" }}>%</span>
                                        </div>
                                    </div>
                                    <div style={{ paddingTop: 8, textAlign: "right" }}>
                                        {(() => {
                                            const rt = questionsConfig?.methodology?.tolerance_weight || 0.4;
                                            const cfl = questionsConfig?.methodology?.capacity_weight || 0.6;
                                            const sum = ((rt + cfl) * 100).toFixed(0);
                                            const valid = Math.abs(rt + cfl - 1) < 0.01;
                                            return <span style={{ fontSize: 12, fontWeight: 600, color: valid ? "#10b981" : "#ef4444" }}>Total: {sum}%</span>;
                                        })()}
                                    </div>
                                </div>

                                {/* SRRI → Portfolio Level Mapping */}
                                <SectionTitle>SRRI → Portfolio Level Mapping</SectionTitle>
                                <p style={{ fontSize: 12, color: "#64748b", marginBottom: 16 }}>
                                    Maps each SRRI score to an allocation matrix portfolio level. This determines which row of the allocation matrix is used for each risk profile.
                                </p>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, maxWidth: 700, marginBottom: 24 }}>
                                    <thead>
                                    <tr style={{ borderBottom: "1px solid #334155" }}>
                                        {["SRRI", "Portfolio Level", "Label"].map(h => (
                                            <th key={h} style={thStyle}>{h}</th>
                                        ))}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {[1, 2, 3, 4, 5, 6, 7].map(srri => {
                                        const levelConfig = questionsConfig?.methodology?.srri_to_portfolio_level || {};
                                        const mapping = levelConfig.mapping || {};
                                        const labels = levelConfig.level_labels || {};
                                        const currentLevel = mapping[String(srri)] ?? (srri <= 2 ? 3 : srri + 1);
                                        return (
                                            <tr key={srri} style={{ borderBottom: "1px solid #1e293b" }}>
                                                <td style={{ padding: "8px", textAlign: "center", fontWeight: 600, color: "#6366f1" }}>{srri}</td>
                                                <td style={{ padding: "8px" }}>
                                                    <select
                                                        value={currentLevel}
                                                        onChange={(e) => {
                                                            const newLevel = parseInt(e.target.value);
                                                            setQuestionsConfig(prev => {
                                                                const prevConfig = prev?.methodology?.srri_to_portfolio_level || {};
                                                                const prevMapping = { ...(prevConfig.mapping || {}) };
                                                                prevMapping[String(srri)] = newLevel;
                                                                return {
                                                                    ...prev,
                                                                    methodology: {
                                                                        ...prev?.methodology,
                                                                        srri_to_portfolio_level: {
                                                                            ...prevConfig,
                                                                            mapping: prevMapping
                                                                        }
                                                                    }
                                                                };
                                                            });
                                                            setDirty(true);
                                                        }}
                                                        style={{ ...cellInput, width: 80, textAlign: "center", cursor: "pointer" }}
                                                    >
                                                        {[3, 4, 5, 6, 7, 8, 9, 10].map(lvl => (
                                                            <option key={lvl} value={lvl}>{lvl}</option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td style={{ padding: "8px" }}>
                                                    <input
                                                        type="text"
                                                        value={labels[String(currentLevel)] || ""}
                                                        onChange={(e) => {
                                                            setQuestionsConfig(prev => {
                                                                const prevConfig = prev?.methodology?.srri_to_portfolio_level || {};
                                                                const prevLabels = { ...(prevConfig.level_labels || {}) };
                                                                prevLabels[String(currentLevel)] = e.target.value;
                                                                return {
                                                                    ...prev,
                                                                    methodology: {
                                                                        ...prev?.methodology,
                                                                        srri_to_portfolio_level: {
                                                                            ...prevConfig,
                                                                            level_labels: prevLabels
                                                                        }
                                                                    }
                                                                };
                                                            });
                                                            setDirty(true);
                                                        }}
                                                        style={{ ...cellInput, width: 160 }}
                                                        placeholder="e.g. Balanced"
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>

                                {/* Visual SRRI Bar */}
                                <div style={{ marginTop: 16, display: "flex", borderRadius: 8, overflow: "hidden", height: 36 }}>
                                    {["Very Low", "Low", "Low-Med", "Medium", "Med-High", "High", "Very High"].map((label, i) => {
                                        const hue = 220 - (i / 6) * 180;
                                        return (
                                            <div key={i} style={{ flex: 1, background: `hsl(${hue}, 60%, 45%)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 600, color: "#fff", textShadow: "0 1px 2px rgba(0,0,0,.4)" }}>
                                                SRRI {i + 1}
                                            </div>
                                        );
                                    })}
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#64748b", marginTop: 4 }}>
                                    <span>Conservative</span><span>Aggressive</span>
                                </div>

                                {/* Circumstance Adjustments */}
                                <div style={{ marginTop: 24 }}>
                                    <SectionTitle>Capacity Adjustment Factors</SectionTitle>
                                    <p style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
                                        Financial circumstances can reduce (never increase) capacity score. Edit these in the Settings tab → Risk Adjustments category.
                                    </p>
                                    <div style={{ background: "#1e293b", borderRadius: 12, padding: 16, border: "1px solid #334155", maxWidth: 500 }}>
                                        <table style={{ width: "100%", fontSize: 11 }}>
                                            <tbody>
                                            <tr><td style={{ padding: "4px 0", color: "#94a3b8" }}>Near retirement (≤5 years)</td><td style={{ textAlign: "right", color: "#f59e0b" }}>×0.75</td></tr>
                                            <tr><td style={{ padding: "4px 0", color: "#94a3b8" }}>Approaching retirement (≤10 years)</td><td style={{ textAlign: "right", color: "#f59e0b" }}>×0.90</td></tr>
                                            <tr><td style={{ padding: "4px 0", color: "#94a3b8" }}>Many dependents (≥3)</td><td style={{ textAlign: "right", color: "#f59e0b" }}>×0.85</td></tr>
                                            <tr><td style={{ padding: "4px 0", color: "#94a3b8" }}>Some dependents (≥1)</td><td style={{ textAlign: "right", color: "#f59e0b" }}>×0.95</td></tr>
                                            <tr><td style={{ padding: "4px 0", color: "#94a3b8" }}>Very tight finances (&lt;10% disposable)</td><td style={{ textAlign: "right", color: "#f59e0b" }}>×0.70</td></tr>
                                            <tr><td style={{ padding: "4px 0", color: "#94a3b8" }}>Tight finances (&lt;20% disposable)</td><td style={{ textAlign: "right", color: "#f59e0b" }}>×0.85</td></tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Settings Tab */}
                        {activeTab === "settings" && (
                            <div>
                                <SectionTitle>System Settings</SectionTitle>
                                <p style={{ fontSize: 12, color: "#64748b", marginBottom: 20 }}>
                                    Configure all engine parameters. Changes sync to the backend and affect calculations in real-time.
                                </p>

                                {/* Category Tabs */}
                                <div style={{ display: "flex", gap: 8, marginBottom: 24, flexWrap: "wrap" }}>
                                    {settingsCategories.map(cat => (
                                        <button
                                            key={cat.id}
                                            onClick={() => setSettingsCategory(cat.id)}
                                            style={{
                                                background: settingsCategory === cat.id ? "#334155" : "#1e293b",
                                                border: settingsCategory === cat.id ? "1px solid #6366f1" : "1px solid #334155",
                                                borderRadius: 8,
                                                padding: "10px 16px",
                                                color: settingsCategory === cat.id ? "#e2e8f0" : "#94a3b8",
                                                fontSize: 12,
                                                fontWeight: 500,
                                                cursor: "pointer",
                                                transition: "all .15s"
                                            }}
                                        >
                                            {cat.icon} {cat.label}
                                        </button>
                                    ))}
                                </div>

                                {/* Settings List */}
                                <div style={{
                                    background: "#1e293b",
                                    borderRadius: 12,
                                    padding: "16px 20px",
                                    border: "1px solid #334155",
                                    maxWidth: 600
                                }}>
                                    {getSettingsByCategory(settingsCategory).map(({ key, meta, value }) => (
                                        <SettingsField
                                            key={key}
                                            settingKey={key}
                                            value={value}
                                            meta={meta}
                                            onChange={updateSetting}
                                        />
                                    ))}
                                </div>

                                {/* Quick Reference */}
                                <div style={{ marginTop: 24, padding: 16, background: "#0c1222", borderRadius: 10, border: "1px solid #1e293b" }}>
                                    <h4 style={{ fontSize: 13, fontWeight: 600, color: "#818cf8", margin: "0 0 12px" }}>Quick Reference</h4>
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, fontSize: 11 }}>
                                        <div>
                                            <div style={{ color: "#64748b", marginBottom: 4 }}>Growth Rate</div>
                                            <div style={{ color: "#10b981", fontWeight: 600, fontSize: 16 }}>
                                                {((settings.projection?.default_growth_rate || 0.04) * 100).toFixed(0)}%
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{ color: "#64748b", marginBottom: 4 }}>Overfunding Threshold</div>
                                            <div style={{ color: "#f59e0b", fontWeight: 600, fontSize: 16 }}>
                                                {((settings.projection?.overfunding_threshold || 1.20) * 100).toFixed(0)}%
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{ color: "#64748b", marginBottom: 4 }}>High Earner Warning</div>
                                            <div style={{ color: "#ef4444", fontWeight: 600, fontSize: 16 }}>
                                                £{(settings.tax?.high_earner_warning_threshold || 175000).toLocaleString()}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>{/* end dark card */}
                </div>{/* end content padding */}

                {/* Toast */}
                {toast && (
                    <div style={{ position: "fixed", bottom: 24, right: 24, background: toast.type === "error" ? "#7f1d1d" : "#064e3b", border: `1px solid ${toast.type === "error" ? "#991b1b" : "#065f46"}`, borderRadius: 10, padding: "12px 20px", color: "#e2e8f0", fontSize: 13, fontWeight: 500, boxShadow: "0 10px 40px rgba(0,0,0,.5)", zIndex: 999 }}>
                        {toast.type === "error" ? "✗ " : "✓ "}{toast.msg}
                    </div>
                )}
            </div>
        </>
    );
};

export default WaiveAdmin;